(() => {
  'use strict';

  const GLOBAL_KEY = '__pbSlideshowA11y';
  const COMPONENT_SELECTOR = 'slideshow-component';
  const SLIDE_SELECTOR = 'slideshow-slide[aria-hidden]';
  const SIZE_GUIDE_SELECTOR = '.pb-pdp-size-guide-scroll';

  const previous = window[GLOBAL_KEY];
  if (previous && typeof previous.destroy === 'function') previous.destroy();

  // Weak ownership prevents an observer callback from retaining its component.
  // The Set is an explicit destroy registry and is pruned for removed subtrees.
  const observerByComponent = new WeakMap();
  const observedComponents = new Set();
  const resizeObserverByGuide = new WeakMap();
  const observedGuides = new Set();
  let discoveryObserver = null;
  let readyListener = null;
  let sizeGuideFrame = null;
  let sizeGuideResizeListener = null;
  let sizeGuideMediaQuery = null;
  let sizeGuideMediaQueryListener = null;

  const syncComponent = (component) => {
    component.querySelectorAll(SLIDE_SELECTOR).forEach((slide) => {
      slide.toggleAttribute('inert', slide.getAttribute('aria-hidden') === 'true');
    });
  };

  const observeComponent = (component) => {
    if (observerByComponent.has(component)) return;
    syncComponent(component);
    const observer = new MutationObserver((records) => {
      const target = records[0]?.target;
      const owner = target?.closest?.(COMPONENT_SELECTOR);
      if (owner && records.some((record) => record.type === 'childList' || record.attributeName === 'aria-hidden')) {
        syncComponent(owner);
      }
    });
    observer.observe(component, {
      subtree: true,
      childList: true,
      attributes: true,
      attributeFilter: ['aria-hidden']
    });
    observerByComponent.set(component, observer);
    observedComponents.add(component);
  };

  const disconnectComponent = (component) => {
    const observer = observerByComponent.get(component);
    if (!observer) return;
    observer.disconnect();
    observerByComponent.delete(component);
    observedComponents.delete(component);
  };

  const disconnectSubtree = (root) => {
    if (!root) return;
    observedComponents.forEach((component) => {
      if (root === component || root.contains?.(component)) disconnectComponent(component);
    });
  };

  const syncSizeGuide = (guide) => {
    const isOverflowing = guide.scrollWidth > guide.clientWidth + 1;
    if (isOverflowing) guide.setAttribute('tabindex', '0');
    else guide.removeAttribute('tabindex');
    guide.toggleAttribute('data-pb-size-guide-overflow', isOverflowing);
  };

  const scheduleSizeGuideSync = () => {
    if (sizeGuideFrame !== null) return;
    const sync = () => {
      sizeGuideFrame = null;
      observedGuides.forEach((guide) => {
        if (guide.isConnected) syncSizeGuide(guide);
      });
    };
    if (typeof requestAnimationFrame === 'function') sizeGuideFrame = requestAnimationFrame(sync);
    else sync();
  };

  const observeSizeGuide = (guide) => {
    if (resizeObserverByGuide.has(guide)) return;
    syncSizeGuide(guide);
    const table = guide.querySelector(':scope > table');
    const details = guide.closest('details');
    const onToggle = () => scheduleSizeGuideSync();
    details?.addEventListener('toggle', onToggle);
    const observers = [];
    if (typeof ResizeObserver === 'function') {
      const observer = new ResizeObserver(() => scheduleSizeGuideSync());
      observer.observe(guide);
      if (table) observer.observe(table);
      observers.push(observer);
    }
    resizeObserverByGuide.set(guide, { observers, details, onToggle });
    observedGuides.add(guide);
    scheduleSizeGuideSync();
  };

  const disconnectSizeGuide = (guide) => {
    const state = resizeObserverByGuide.get(guide);
    state?.observers.forEach((observer) => observer.disconnect());
    state?.details?.removeEventListener('toggle', state.onToggle);
    resizeObserverByGuide.delete(guide);
    observedGuides.delete(guide);
  };

  const disconnectSizeGuideSubtree = (root) => {
    if (!root) return;
    observedGuides.forEach((guide) => {
      if (root === guide || root.contains?.(guide)) disconnectSizeGuide(guide);
    });
  };

  const discover = (root) => {
    if (!root || typeof root.querySelectorAll !== 'function') return;
    if (root.matches?.(COMPONENT_SELECTOR)) observeComponent(root);
    root.querySelectorAll(COMPONENT_SELECTOR).forEach(observeComponent);
    if (root.matches?.(SIZE_GUIDE_SELECTOR)) observeSizeGuide(root);
    root.querySelectorAll(SIZE_GUIDE_SELECTOR).forEach(observeSizeGuide);
  };

  const destroy = () => {
    observedComponents.forEach(disconnectComponent);
    observedGuides.forEach(disconnectSizeGuide);
    if (discoveryObserver) discoveryObserver.disconnect();
    discoveryObserver = null;
    document.removeEventListener('shopify:section:load', onSectionLoad);
    document.removeEventListener('shopify:section:unload', onSectionUnload);
    document.removeEventListener('shopify:block:select', onBlockSelect);
    if (readyListener) document.removeEventListener('DOMContentLoaded', readyListener);
    readyListener = null;
    if (sizeGuideFrame !== null && typeof cancelAnimationFrame === 'function') cancelAnimationFrame(sizeGuideFrame);
    sizeGuideFrame = null;
    if (sizeGuideResizeListener) window.removeEventListener('resize', sizeGuideResizeListener);
    if (sizeGuideMediaQuery && sizeGuideMediaQueryListener) {
      if (typeof sizeGuideMediaQuery.removeEventListener === 'function') sizeGuideMediaQuery.removeEventListener('change', sizeGuideMediaQueryListener);
      else if (typeof sizeGuideMediaQuery.removeListener === 'function') sizeGuideMediaQuery.removeListener(sizeGuideMediaQueryListener);
    }
    sizeGuideResizeListener = null;
    sizeGuideMediaQuery = null;
    sizeGuideMediaQueryListener = null;
  };

  const onSectionLoad = (event) => discover(event.target);
  const onSectionUnload = (event) => {
    disconnectSubtree(event.target);
    disconnectSizeGuideSubtree(event.target);
  };
  const onBlockSelect = (event) => discover(event.target);

  window[GLOBAL_KEY] = { destroy };

  const start = () => {
    discover(document);
    sizeGuideResizeListener = () => scheduleSizeGuideSync();
    window.addEventListener('resize', sizeGuideResizeListener, { passive: true });
    if (typeof window.matchMedia === 'function') {
      sizeGuideMediaQuery = window.matchMedia('(max-width: 749px)');
      sizeGuideMediaQueryListener = () => scheduleSizeGuideSync();
      if (typeof sizeGuideMediaQuery.addEventListener === 'function') sizeGuideMediaQuery.addEventListener('change', sizeGuideMediaQueryListener);
      else if (typeof sizeGuideMediaQuery.addListener === 'function') sizeGuideMediaQuery.addListener(sizeGuideMediaQueryListener);
    }
    discoveryObserver = new MutationObserver((records) => {
      records.forEach((record) => {
        record.removedNodes.forEach((node) => {
          disconnectSubtree(node);
          disconnectSizeGuideSubtree(node);
        });
        record.addedNodes.forEach((node) => discover(node));
      });
    });
    discoveryObserver.observe(document.documentElement, { childList: true, subtree: true });
    document.addEventListener('shopify:section:load', onSectionLoad);
    document.addEventListener('shopify:section:unload', onSectionUnload);
    document.addEventListener('shopify:block:select', onBlockSelect);
  };

  if (document.readyState === 'loading') {
    readyListener = start;
    document.addEventListener('DOMContentLoaded', readyListener, { once: true });
  } else {
    start();
  }
})();
